import { AutoAwesome, Add } from "@mui/icons-material";
import {
  Box, Button, Card, CardContent, CircularProgress,
  Collapse, FormControlLabel, Switch, Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useUserProjects } from "../../query/apiQueries";
import { enableOrgAI, listAIConfigs, deleteAIConfig } from "../../modules/aiConfig";
import AIKeyTable from "./AiKeyTable";
import KeyModal from "./KeyModal";

const resolveAddedBy = (createdById, user) => {
  if (!createdById || user?.id !== createdById) return '—';
  return [user?.first_name, user?.last_name].filter(Boolean).join(' ') || 'You';
};

const mapFeatures = (features = []) =>
  features.map((f) => ({
    featureKey: f.feature_key ?? f.featureKey,
    isEnabled: f.is_enabled ?? f.isEnabled ?? true,
    maxResults: f.max_results ?? f.maxResults ?? null,
    requestPerDay: f.request_per_day ?? f.requestPerDay ?? null,
  }));

const mapToEntry = (c, applyTo, projectIds, createdBy, user) => ({
  id: c.id,
  llm: c.provider ?? 'gemini',
  provider: c.provider,
  model: c.model,
  applyTo,
  projectIds,
  apiKey: c.api_key ?? '',
  features: mapFeatures(c.features),
  addedBy: resolveAddedBy(createdBy, user),
  createdAt: c.created_at,
});


const OrgAIConfiguration = ({ user }) => {
  const { data: projectData } = useUserProjects({
    perPage: "all", page: 1, search: null,
    includeTestCases: false, includeLinkedProjects: true,
  });

  const projectOptions = projectData?.projects?.map(({ id, name }) => ({ id, name })) ?? [];

  const [enabled, setEnabled] = useState(false);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingAI, setTogglingAI] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editEntry, setEditEntry] = useState(null);

  const fetchConfigs = async () => {
    setLoading(true);
    try {
      const res = await listAIConfigs();
      setEnabled(!!res.is_ai_enabled);

      if (!res.is_ai_enabled) { setEntries([]); return; }

      const mapped = [];

      if (res.org_config) {
        const c = res.org_config;
        mapped.push(mapToEntry(c, 'org', [], c.created_by, user));
      }

      (res.project_configs ?? []).forEach((pc) => {
        const firstProject = pc.projects?.[0];
        if (!firstProject) return;
        mapped.push({
          ...mapToEntry(pc, 'projects', pc.projects.map((p) => p.project_id), firstProject.created_by, user),
          id: firstProject.id,
          createdAt: firstProject.created_at,
          isEnabled: pc.is_enabled ?? true,
        });
      });

      setEntries(mapped);
    } catch {
      // fail silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchConfigs(); }, []);

  const handleToggle = async (checked) => {
    setTogglingAI(true);
    try {
      await enableOrgAI(user?.organization_id, checked);
      setEnabled(checked);
      await fetchConfigs();
    } finally {
      setTogglingAI(false);
    }
  };

  const openAdd = () => { setEditEntry(null); setModalOpen(true); };
  const openEdit = (entry) => { setEditEntry(entry); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditEntry(null); };

  const handleSaveEntry = (savedConfigs, isEdit, editedEntryId) => {
    if (!savedConfigs.length) return;

    const first = savedConfigs[0];
    const isOrg = first.project_id === null;
    const grouped = {
      ...mapToEntry(first, isOrg ? 'org' : 'projects',
        isOrg ? [] : savedConfigs.map((c) => c.project_id),
        first.created_by, user),
    };

    setEntries((prev) =>
      isEdit ? prev.map((e) => (e.id === editedEntryId ? grouped : e)) : [...prev, grouped]
    );
  };

  const handleDelete = async (id) => {
    const entry = entries.find((e) => e.id === id);
    if (!entry) return;
    try {
      await deleteAIConfig(entry.applyTo === 'org' ? [] : entry.projectIds);
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch {
      // fail silently
    }
  };

  const isBusy = togglingAI || loading;

  return (
    <>
      <Card variant="outlined" sx={{ borderRadius: 2, mt: 3 }}>
        <CardContent sx={{ p: 3 }}>

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AutoAwesome sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography variant="h6" fontWeight={600}>AI Configuration</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isBusy && <CircularProgress size={16} />}
              <FormControlLabel
                control={
                  <Switch checked={enabled} onChange={(e) => handleToggle(e.target.checked)}
                    disabled={isBusy} size="small" />
                }
                label={<Typography variant="body2" fontWeight={500}>{enabled ? "Enabled" : "Disabled"}</Typography>}
                labelPlacement="start"
                sx={{ mr: 0 }}
              />
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Configure AI API keys and features for test case generation across your projects.
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <Collapse in={enabled}>
              <Box>
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1.5 }}>
                  <Button size="small" variant="contained" startIcon={<Add sx={{ fontSize: 14 }} />} onClick={openAdd}>
                    Add API Key
                  </Button>
                </Box>
                <AIKeyTable entries={entries} onEdit={openEdit} onDelete={handleDelete}
                  showProject projects={projectOptions} emptyLabel="No API keys configured yet." />
              </Box>
            </Collapse>
          )}

        </CardContent>
      </Card>

      <KeyModal open={modalOpen} onClose={closeModal} onSave={handleSaveEntry}
        existingEntries={entries} editEntry={editEntry} showProject projects={projectOptions} />
    </>
  );
};

export default OrgAIConfiguration;